import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

type SeoProps = {
  title: string;
  description: string;
  pathname?: string;
  image?: string;
  imageAlt?: string;
  openGraphTitle?: string;
  openGraphDescription?: string;
  type?: 'website' | 'article';
};

type SeoQueryResult = {
  site: {
    siteMetadata: {
      siteUrl: string;
    };
  };
};

const DEFAULT_SITE_NAME = 'EContra®';

const toAbsoluteUrl = (siteUrl: string, path = '/') => {
  const normalizedSiteUrl = siteUrl.endsWith('/') ? siteUrl.slice(0, -1) : siteUrl;

  if (!path || path === '/') {
    return normalizedSiteUrl;
  }

  return new URL(path.replace(/^\//, ''), `${normalizedSiteUrl}/`).toString();
};

function Seo({
  title,
  description,
  pathname = '/',
  image,
  imageAlt,
  openGraphTitle,
  openGraphDescription,
  type = 'website',
}: SeoProps) {
  const data = useStaticQuery<SeoQueryResult>(graphql`
    query SeoMetadata {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const { siteUrl } = data.site.siteMetadata;
  const canonicalUrl = toAbsoluteUrl(siteUrl, pathname);
  const imageUrl = image ? toAbsoluteUrl(siteUrl, image) : undefined;
  const socialTitle = openGraphTitle ?? title;
  const socialDescription = openGraphDescription ?? description;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:site_name" content={DEFAULT_SITE_NAME} />
      <meta property="og:type" content={type} />
      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      {imageUrl ? <meta property="og:image:width" content="1200" /> : null}
      {imageUrl ? <meta property="og:image:height" content="630" /> : null}
      {imageUrl ? <meta property="og:image:alt" content={imageAlt ?? DEFAULT_SITE_NAME} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      {imageUrl ? <meta name="twitter:image:alt" content={imageAlt ?? DEFAULT_SITE_NAME} /> : null}
    </>
  );
}

export default Seo;
